import { Comment } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/comment_pb';
import { Metadata } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/entity_pb';
import { ExperimentParameters } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_pb';
import {
  CreateExperimentCommentRequest,
  ListExperimentCommentsRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import {
  Order,
  SortingRequest,
  SortProperty,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import { Property } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesLink,
  buttonStylesPrimary,
  ClassNamesFromIStyles,
  CodeBlock,
  IH2OTheme,
  Link,
  linkStylesBlack,
  Loader,
  Pivot,
  TextField,
  textFieldStylesEditor,
  TextWithCopy,
  textWithCopyStylesPlain,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';

import { useUsers } from '../../authz/providers/UsersProvider';
import { DateShort } from '../../components/Date/DateShort';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { ENDPOINTS } from './constants';
import { useGranularMLOpsPermissions } from './userActions';
type CommentItem = Comment & {
  createdByName?: string;
};
interface IModelMetadataStyles {
  container: IStyle;
  metadata: IStyle;
  metadataExperimentId: IStyle;
  schemaTable: IStyle;
  comment: IStyle;
  commentUser: IStyle;
  commentMessage: IStyle;
  commentTime: IStyle;
  loadMore: IStyle;
  loadMoreLoader: IStyle;
  loader: IStyle;
}
export const modelMetadataStyles = (theme: IH2OTheme): Partial<IModelMetadataStyles> => ({
  container: {
    padding: 20,
    borderRadius: 10,
  },
  comment: {
    marginBottom: 12,
    padding: 10,
    background: theme.semanticColors?.bodyBackground,
    borderRadius: 8,
  },
  commentUser: { fontWeight: 'bold', marginBottom: 4 },
  commentMessage: {
    fontSize: 13,
  },
  commentTime: {
    fontSize: 10,
    color: theme.semanticColors?.textSecondary,
    marginTop: 6,
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0',
    borderWidth: 1,
    borderTopStyle: 'solid',
    borderColor: theme.semanticColors?.inputBorder,
    borderRadius: 4,
    // TODO: Use theme colors.
    backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
    '&:hover': {
      borderColor: theme.semanticColors?.buttonPrimaryBackground,
      cursor: 'pointer',
      button: {
        textDecoration: 'none',
      },
      'button:active': {
        textDecoration: 'none',
      },
    },
    span: {
      color: theme.semanticColors?.textSecondary,
    },
    '&:hover span': {
      color: theme.semanticColors?.textPrimary,
    },
  },
  loadMoreLoader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
  },
  metadata: {
    marginBottom: 20,
  },
  metadataExperimentId: { fontSize: 12, marginBottom: 10 },
  schemaTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 20,
    '& th, & td': {
      border: `1px solid ${theme.semanticColors?.inputBorder}`,
      padding: 8,
      textAlign: 'left',
    },
    '& th': {
      // TODO: Theme this.
      backgroundColor: '#f4f4f4',
    },
    '& tr:hover': {
      // TODO: Theme this.
      backgroundColor: '#f9f9f9',
    },
  },
  loader: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const snakeToText = (snakeStr: string) => {
    return snakeStr
      .split('_') // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter
      .join(' '); // Join words with spaces
  },
  getParsedJson = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error(e);
      return null;
    }
  };

const flexStyles = {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  fillStyles = {
    pivotContainerStyles: {
      root: {
        '.ms-Pivot-wrapper': flexStyles,
        ...flexStyles,
      },
    },
    styles: {
      itemContainer: {
        '> div': flexStyles,
        ...flexStyles,
      },
      root: {
        margin: '0px 20px',
      },
    },
  };

const ModelMetadataView = ({
  experimentId,
  metadata,
  parameters,
}: {
  experimentId: string;
  metadata?: Metadata;
  parameters?: ExperimentParameters;
}) => {
  const theme = useTheme(),
    classNames = useClassNames<IModelMetadataStyles, ClassNamesFromIStyles<IModelMetadataStyles>>(
      'modelMetadata',
      modelMetadataStyles(theme)
    ),
    { ACTIVE_WORKSPACE_NAME } = useWorkspaces(),
    permissions = useGranularMLOpsPermissions(ACTIVE_WORKSPACE_NAME),
    { usersNameMap } = useUsers(),
    { addToast } = useToast(),
    [nextPageToken, setNextPageToken] = React.useState<Uint8Array>(),
    [isLoadingMore, setIsLoadingMore] = React.useState(false),
    [loadingComments, setLoadingComments] = React.useState(true),
    [comments, setComments] = React.useState<CommentItem[]>(),
    [newComment, setNewComment] = React.useState(''),
    [commentsOrderDesc, setCommentsOrderDesc] = React.useState(true),
    [commentsContainerHeight, setCommentsContainerHeight] = React.useState(187),
    commentsContainerRef = React.useRef<HTMLDivElement>(null),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    loadStateRef = React.useRef({
      listingComments: false,
      creatingComment: false,
    }),
    evaluateLoading = () => {
      if (!loadStateRef.current.listingComments && !loadStateRef.current.creatingComment) {
        setLoadingComments(false);
      }
    },
    experimentClient = createClient(ExperimentService, storageTransport),
    [selectedKey, setSelectedKey] = React.useState<'0' | '1'>('0'),
    listExperimentComments = React.useCallback(
      async (experimentId: string, pageToken?: Uint8Array, newestFirst = true) => {
        if (!experimentId) return;
        if (pageToken) {
          setIsLoadingMore(true);
        } else {
          loadStateRef.current.listingComments = true;
          setLoadingComments(true);
        }
        const request = new ListExperimentCommentsRequest({
          experimentId,
          paging: {
            pageSize: 10,
            pageToken,
          },
          sorting: new SortingRequest({
            property: [
              new SortProperty({
                property: new Property({
                  propertyType: {
                    case: 'field',
                    value: 'created_time',
                  },
                }),
                order: newestFirst ? Order.DESCENDING : Order.ASCENDING,
              }),
            ],
          }),
        });
        try {
          const response = await experimentClient.listExperimentComments(request);
          setNextPageToken(response?.paging?.nextPageToken);
          if (response) {
            const newComments = response?.comment?.map(
              (comment) =>
                ({
                  ...comment,
                  createdByName: usersNameMap?.[`users/${comment.authorId}`]?.displayName || comment.authorId,
                } as CommentItem)
            );
            setComments((comments) => (pageToken ? [...(comments || []), ...(newComments || [])] : newComments));
          }
        } catch (err) {
          const message = `Error listing experiment comments: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.listingComments = false;
          evaluateLoading();
          setIsLoadingMore(false);
        }
      },
      [usersNameMap]
    ),
    createExperimentComment = React.useCallback(
      async (experimentId: string, commentMessage: string) => {
        if (!experimentId) return;
        loadStateRef.current.creatingComment = true;
        const request = new CreateExperimentCommentRequest({
          experimentId,
          commentMessage,
        });
        try {
          await experimentClient.createExperimentComment(request);
          listExperimentComments(experimentId);
        } catch (err) {
          const message = `Error creating experiment comment: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.creatingComment = false;
          evaluateLoading();
        }
      },
      [experimentClient]
    );

  React.useEffect(() => {
    if (experimentId && permissions?.isAbleToListExperimentComments) {
      listExperimentComments(experimentId, undefined, commentsOrderDesc);
    }
  }, [experimentId, listExperimentComments, commentsOrderDesc, permissions?.isAbleToListExperimentComments]);

  return (
    <Pivot
      {...fillStyles}
      onLinkClick={(item) => setSelectedKey((item?.props?.itemKey as '0' | '1') || '0')}
      selectedKey={selectedKey}
      placeholder={undefined}
      items={[
        {
          headerText: 'Comments',
          content: (
            <div className={classNames.container} ref={commentsContainerRef}>
              {loadingComments ? (
                <div
                  className={classNames.loader}
                  style={{
                    height: commentsContainerHeight,
                  }}
                >
                  <Loader label="Loading comments..." />
                </div>
              ) : (
                <>
                  <Button
                    text={commentsOrderDesc ? 'Newest first' : 'Oldest first'}
                    styles={[
                      buttonStylesLink,
                      {
                        rootExpanded: { borderColor: 'transparent', backgroundColor: 'transparent' },
                        rootDisabled: {
                          background: 'transparent',
                          borderColor: 'transparent',
                        },
                      },
                    ]}
                    disabled={!comments?.length}
                    menuProps={{
                      items: commentsOrderDesc
                        ? [
                            {
                              key: 'oldest-first',
                              text: 'Oldest first',
                              onClick: () => {
                                setCommentsContainerHeight(
                                  // 40 is the container padding.
                                  (commentsContainerRef.current?.clientHeight || 40) - 40 || 187
                                );
                                setCommentsOrderDesc(false);
                                listExperimentComments(experimentId, undefined, false);
                              },
                            },
                          ]
                        : [
                            {
                              key: 'newest-first',
                              text: 'Newest first',
                              onClick: () => {
                                setCommentsContainerHeight(
                                  (commentsContainerRef.current?.clientHeight || 40) - 40 || 187
                                );
                                setCommentsOrderDesc(true);
                                listExperimentComments(experimentId, undefined, true);
                              },
                            },
                          ],
                      calloutProps: {
                        styles: {
                          root: {
                            padding: '8px 0px',
                          },
                        },
                      },
                    }}
                  />
                  {!permissions?.isAbleToListExperimentComments ? (
                    <p>You dont have permissions to view comments. Ask the workspace owner to grant you access.</p>
                  ) : comments?.length ? (
                    <div style={{ marginBottom: 16, cursor: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                      {comments.map((comment, idx) => {
                        const createdTimeIso = (comment.createdTime?.toJson() as string) || ``;
                        return (
                          <div key={`comment-${idx}`} className={classNames.comment}>
                            <div className={classNames.commentUser}>{comment.createdByName}</div>
                            <div className={classNames.commentMessage}>{comment.message}</div>
                            <div className={classNames.commentTime}>
                              {createdTimeIso && <DateShort date={createdTimeIso} />}
                            </div>
                          </div>
                        );
                      })}
                      {nextPageToken &&
                        (isLoadingMore ? (
                          <Loader className={classNames.loadMoreLoader} />
                        ) : (
                          <div
                            className={classNames.loadMore}
                            onClick={() => listExperimentComments(experimentId, nextPageToken, commentsOrderDesc)}
                          >
                            <Link styles={linkStylesBlack}>Load more comments</Link>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p>No comments yet.</p>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {permissions?.isAbleToCreateExperimentComment ? (
                      <>
                        <TextField
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(_ev, newValue) => setNewComment(newValue || '')}
                          multiline
                          resizable
                          styles={textFieldStylesEditor}
                        />
                        <Button
                          text="Add comment"
                          onClick={() => {
                            createExperimentComment(experimentId, newComment);
                            setNewComment('');
                          }}
                          disabled={!newComment.trim()}
                          styles={buttonStylesPrimary}
                        />
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ),
        },
        {
          headerText: 'Parameters',
          content: (
            <div className={classNames.container}>
              {parameters ? (
                <table className={classNames.schemaTable}>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(parameters).map(([key, value]) => (
                      <tr key={key}>
                        <td>{snakeToText(key)}</td>
                        <td>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No parameters to display.</p>
              )}
            </div>
          ),
        },
        {
          headerText: 'Metadata',
          content: (
            <div className={classNames.container}>
              <div className={classNames.metadata}>
                <div className={classNames.metadataExperimentId}>
                  <b>Experiment ID:</b>{' '}
                  <TextWithCopy
                    text={experimentId}
                    styles={{ ...textWithCopyStylesPlain, ...{ contents: { fontSize: 12 } } }}
                  />
                </div>
                {metadata ? (
                  <>
                    <>
                      <table className={classNames.schemaTable}>
                        <thead>
                          <tr>
                            <th>Metadata key</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(metadata?.values)
                            ?.filter((value) => value?.[1].value?.case === 'stringValue')
                            .map((value) => (
                              <tr key={value?.[0]}>
                                <td>{snakeToText(value?.[0])}</td>
                                <td>{value?.[1].value?.value}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </>
                    {Object.entries(metadata?.values)
                      ?.filter((value) => value?.[1].value?.case === 'jsonValue')
                      .map((value) => {
                        if (value?.[1].value?.case === 'jsonValue') {
                          const parsedJson = getParsedJson(value?.[1].value?.value);
                          if (
                            Object.values(parsedJson?.[0] || {})?.length === 2 &&
                            parsedJson?.[0]?.name &&
                            parsedJson?.[0]?.type
                          ) {
                            return (
                              <>
                                <h3>{snakeToText(value?.[0])}</h3>
                                <table className={classNames.schemaTable}>
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {parsedJson
                                      ? parsedJson?.map((field: { name: string; type: string }, index: number) => (
                                          <tr key={index}>
                                            <td>{field.name}</td>
                                            <td>{field.type}</td>
                                          </tr>
                                        ))
                                      : null}
                                  </tbody>
                                </table>
                              </>
                            );
                          } else {
                            return (
                              <>
                                <h3>{snakeToText(value?.[0])}</h3>
                                <CodeBlock showCopyButton>{JSON.stringify(parsedJson)}</CodeBlock>
                              </>
                            );
                          }
                        }
                        return null;
                      })}
                  </>
                ) : (
                  <p>No metadata do display.</p>
                )}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
};

export default ModelMetadataView;
